// AI Service for SpeakRight - DeepSeek API Integration
const DEEPSEEK_API_KEY = 'sk-7549b29d010a424a836d081a478552f4';
const DEEPSEEK_API_URL = 'https://api.deepseek.com/v1/chat/completions';

class AIService {
  constructor() {
    this.apiKey = DEEPSEEK_API_KEY;
    this.baseURL = DEEPSEEK_API_URL;
  }

  async makeRequest(messages, options = {}) {
    try {
      const response = await fetch(this.baseURL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: 'deepseek-chat',
          messages: messages,
          temperature: options.temperature || 0.7,
          max_tokens: options.maxTokens || 1000,
          stream: false
        })
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const data = await response.json();
      return data.choices[0].message.content;
    } catch (error) {
      console.error('AI Service Error:', error);
      throw error;
    }
  }

  // AI Tutor functionality
  async getTutorResponse(userMessage, context = {}) {
    const systemPrompt = `You are an expert English tutor for Chinese speakers. You help students learn English through:
1. Clear explanations in both English and Chinese
2. Grammar corrections with examples
3. Vocabulary building
4. Pronunciation tips
5. Cultural context when relevant

Current context: ${JSON.stringify(context)}

Respond in a helpful, encouraging tone. Use simple English when possible, but provide Chinese translations for complex concepts.`;

    const messages = [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: userMessage }
    ];

    return await this.makeRequest(messages, { temperature: 0.8 });
  }

  // Pronunciation analysis
  async analyzePronunciation(word, userAttempt, context = {}) {
    const systemPrompt = `You are a pronunciation expert. Analyze the user's pronunciation attempt and provide:
1. Accuracy score (0-100)
2. Specific pronunciation issues
3. Tips for improvement
4. Mouth position guidance
5. Practice suggestions

Word: ${word}
User attempt: ${userAttempt}
Context: ${JSON.stringify(context)}

Provide detailed feedback in both English and Chinese.`;

    const messages = [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: `Please analyze my pronunciation of "${word}". I said: "${userAttempt}"` }
    ];

    return await this.makeRequest(messages, { temperature: 0.6 });
  }

  // Generate learning exercises
  async generateExercises(word, difficulty = 'beginner', exerciseType = 'vocabulary') {
    const systemPrompt = `Generate English learning exercises for the word "${word}" with difficulty "${difficulty}" and type "${exerciseType}".

Create 3 different exercises:
1. Fill in the blank
2. Multiple choice
3. Sentence construction

Include:
- Clear instructions
- Answer keys
- Difficulty progression
- Chinese translations for instructions

Format as JSON with exercise objects.`;

    const messages = [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: `Generate exercises for "${word}"` }
    ];

    return await this.makeRequest(messages, { temperature: 0.7 });
  }

  // Personalized learning path
  async generateLearningPath(userLevel, interests = [], goals = []) {
    const systemPrompt = `Create a personalized English learning path for a Chinese speaker.

User Level: ${userLevel}
Interests: ${interests.join(', ')}
Goals: ${goals.join(', ')}

Provide:
1. Recommended learning sequence
2. Daily/weekly goals
3. Practice activities
4. Progress milestones
5. Estimated timeline

Format as a structured learning plan with clear steps.`;

    const messages = [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: 'Create my personalized learning path' }
    ];

    return await this.makeRequest(messages, { temperature: 0.6 });
  }

  // Grammar correction
  async correctGrammar(text, context = {}) {
    const systemPrompt = `You are a grammar expert. Correct the following English text and explain:
1. What was wrong
2. Why it's wrong
3. The correct version
4. Grammar rules involved

Provide explanations in both English and Chinese.`;

    const messages = [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: `Please correct this text: "${text}"` }
    ];

    return await this.makeRequest(messages, { temperature: 0.5 });
  }

  // Vocabulary explanation
  async explainVocabulary(word, context = {}) {
    const systemPrompt = `Explain the vocabulary word "${word}" in detail:
1. Definition in English and Chinese
2. Part of speech
3. Pronunciation guide
4. Example sentences (3-5)
5. Synonyms and antonyms
6. Common collocations
7. Usage tips

Make it comprehensive but easy to understand.`;

    const messages = [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: `Explain the word "${word}"` }
    ];

    return await this.makeRequest(messages, { temperature: 0.6 });
  }

  // Conversation practice
  async generateConversation(topic, level = 'beginner') {
    const systemPrompt = `Generate a natural English conversation for ${level} level learners about "${topic}".

Include:
1. 6-8 exchanges between two people
2. Natural, everyday language
3. Common expressions and phrases
4. Chinese translations
5. Key vocabulary highlighted
6. Discussion questions

Make it engaging and practical.`;

    const messages = [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: `Generate a conversation about "${topic}"` }
    ];

    return await this.makeRequest(messages, { temperature: 0.8 });
  }

  // Progress analysis
  async analyzeProgress(userData, timePeriod = 'week') {
    const systemPrompt = `Analyze the user's English learning progress and provide:
1. Strengths and improvements
2. Areas needing attention
3. Recommended next steps
4. Motivation and encouragement
5. Study tips

User Data: ${JSON.stringify(userData)}
Time Period: ${timePeriod}

Be encouraging and specific in your feedback.`;

    const messages = [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: 'Analyze my learning progress' }
    ];

    return await this.makeRequest(messages, { temperature: 0.7 });
  }
}

// Create singleton instance
const aiService = new AIService();

export default aiService;
